import {
  CircleDot,
  Compass,
  Dices,
  Magnet,
  MousePointerClick,
  Network,
  SlidersHorizontal,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { useStore } from "../app/store.ts";
import { DebugMenu } from "./DebugMenu.tsx";
import { HamburgerMenu } from "./HamburgerMenu.tsx";
import { HierarchyPanelBody } from "./HierarchyPanel.tsx";
import { OperatorsIcon, UndoIcon } from "./Icons.tsx";
import {
  CheckerDeselectIcon,
  ClearSelectionIcon,
  InvertSelectionIcon,
  MeshMenuIcon,
  SelectAllIcon,
  SelectConnectedIcon,
  SelectLessIcon,
  SelectMoreIcon,
} from "./icons3d.tsx";
import { MenuItem } from "./menu/MenuItem.tsx";
import { MenuDivider } from "./menu/MenuPrimitives.tsx";
import { Popover } from "./Popover.tsx";
import { OperatorsPanelBody, OptionsPanelBody } from "./panelRegistry.tsx";
import { Slider } from "./Slider.tsx";
import {
  COLOR_BORDER,
  COLOR_HEADER_BG,
  COLOR_TEXT_DIM,
  FONT_SIZE_TINY,
  RADIUS_HEADER,
  useChromeDim,
  Z_TOOLBAR,
} from "./tokens.ts";
import { Tool } from "./tool/Tool.tsx";
import { ToolSep } from "./tool/ToolSep.tsx";
import { ToolSurface } from "./tool/ToolSurface.tsx";

// ---------------------------------------------------------------------------
// Container constants — matches the ToolbarStrip / Sidebar L-shape
// ---------------------------------------------------------------------------

const HEADER_BG = COLOR_HEADER_BG;
const HEADER_HEIGHT = 44;
const CORNER_RADIUS = RADIUS_HEADER;

// ---------------------------------------------------------------------------
// OptionsButton — header popover for pivot / snap / influence settings
// ---------------------------------------------------------------------------

function OptionsButton() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const snapEnabled = useStore((s) => s.snapEnabled);
  const proportionalEnabled = useStore((s) => s.proportionalEnabled);
  const mode = useStore((s) => s.mode);
  const badges: React.ReactNode[] = [];
  if (snapEnabled) badges.push(<Magnet size={10} />);
  if (proportionalEnabled && mode === "edit")
    badges.push(<CircleDot size={10} />);
  return (
    <div
      ref={anchorRef}
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      <Tool
        icon={<SlidersHorizontal size={13} />}
        tip="Options"
        active={open}
        badges={badges}
        onActivate={() => setOpen((p) => !p)}
      />
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          minWidth: 320,
          padding: "10px 14px",
          borderRadius: RADIUS_HEADER,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <OptionsPanelBody />
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OperatorsButton — header popover for context-aware operator list
// ---------------------------------------------------------------------------

function OperatorsButton() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={anchorRef}
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      <Tool
        icon={<OperatorsIcon size={13} />}
        tip={`Operators (${getShortcutLabel("toggleOperatorSearch")})`}
        active={open}
        onActivate={() => setOpen((p) => !p)}
      />
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          minWidth: 320,
          padding: "10px 14px",
          borderRadius: RADIUS_HEADER,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <OperatorsPanelBody />
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HierarchyButton — header popover for scene hierarchy
// ---------------------------------------------------------------------------

function HierarchyButton() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={anchorRef}
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      <Tool
        icon={<Network size={13} />}
        tip="Scene Hierarchy"
        active={open}
        onActivate={() => setOpen((p) => !p)}
      />
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          minWidth: 320,
          padding: "10px 14px",
          borderRadius: RADIUS_HEADER,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <HierarchyPanelBody />
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SelectionButton — header popover for selection operations
// ---------------------------------------------------------------------------

function SelectionButton() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const selectAll = useStore((s) => s.selectAll);
  const invertSelection = useStore((s) => s.invertSelection);
  const checkerDeselect = useStore((s) => s.checkerDeselect);
  const clearSelection = useStore((s) => s.clearSelection);
  const recallSelection = useStore((s) => s.recallSelection);
  const randomSelect = useStore((s) => s.randomSelect);
  const selectMore = useStore((s) => s.selectMore);
  const selectLess = useStore((s) => s.selectLess);
  const selectConnectedFromSelection = useStore(
    (s) => s.selectConnectedFromSelection,
  );
  const selectSimilarFacesByNormal = useStore(
    (s) => s.selectSimilarFacesByNormal,
  );
  const pushUndo = useStore((s) => s.pushUndo);
  const hasEditSelection = useStore(
    (s) => (s.editMode?.selectedVertices.size ?? 0) > 0,
  );
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const canCheckerDeselect = useStore((s) => {
    if (s.workspaceMode !== "edit" || !s.editMode) return false;
    const em = s.editMode;
    if (em.granularity === "face") return em.selectedFaces.size > 1;
    if (em.granularity === "edge") return em.selectedEdges.size > 1;
    return em.selectedVertices.size > 1;
  });
  const canSelectSimilarByNormal = useStore((s) => {
    if (s.workspaceMode !== "edit" || !s.editMode) return false;
    return (
      s.editMode.granularity === "face" && s.editMode.selectedFaces.size > 0
    );
  });
  const inEditMode = useStore((s) => s.workspaceMode === "edit");
  const workspaceMode = useStore((s) => s.workspaceMode);
  const hasRecallable = useStore((s) => {
    if (s.workspaceMode === "edit") {
      const last = s.lastEditModeSelection;
      return last !== null && last.objectId === s.editMode?.objectId;
    }
    return (s.lastObjectModeSelection?.size ?? 0) > 0;
  });
  const enabled = workspaceMode === "object" || workspaceMode === "edit";

  const [randomExpanded, setRandomExpanded] = useState(false);
  const [randomCoverage, setRandomCoverage] = useState(50);

  const close = useCallback(() => {
    setOpen(false);
    setRandomExpanded(false);
  }, []);

  const handleRandomCoverageChange = useCallback(
    (value: number) => {
      setRandomCoverage(value);
      randomSelect(value / 100);
    },
    [randomSelect],
  );

  return (
    <div
      ref={anchorRef}
      style={{ display: "flex", alignItems: "center", position: "relative" }}
    >
      <Tool
        icon={<MousePointerClick size={13} />}
        tip="Selection"
        active={open}
        disabled={!enabled}
        onActivate={() => setOpen((p) => !p)}
      />
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={close}
        style={{ minWidth: 200 }}
      >
        <MenuItem
          label="Select All"
          icon={<SelectAllIcon size={13} />}
          shortcut={getShortcutLabel("selectAll") ?? "Ctrl+A"}
          onClick={() => {
            selectAll();
            close();
          }}
        />
        <MenuItem
          label="Invert Selection"
          icon={<InvertSelectionIcon size={13} />}
          onClick={() => {
            invertSelection();
            close();
          }}
        />
        {inEditMode && (
          <>
            <MenuItem
              label="Checker Deselect"
              icon={<CheckerDeselectIcon size={13} />}
              onClick={() => {
                checkerDeselect();
                close();
              }}
              disabled={!canCheckerDeselect}
            />
            <MenuItem
              label="Select More"
              icon={<SelectMoreIcon size={13} />}
              shortcut={getShortcutLabel("selectMore") ?? "Ctrl+="}
              onClick={() => {
                selectMore();
                close();
              }}
            />
            <MenuItem
              label="Select Less"
              icon={<SelectLessIcon size={13} />}
              shortcut={getShortcutLabel("selectLess") ?? "Ctrl+-"}
              onClick={() => {
                selectLess();
                close();
              }}
            />
            <MenuItem
              label="Select Connected"
              icon={<SelectConnectedIcon size={13} />}
              shortcut={getShortcutLabel("select_connected") ?? "L"}
              onClick={() => {
                pushUndo();
                selectConnectedFromSelection();
                close();
              }}
              disabled={!hasEditSelection}
            />
            <MenuItem
              label="Select Similar: Normal"
              icon={<Compass size={13} />}
              onClick={() => {
                pushUndo();
                selectSimilarFacesByNormal();
                close();
              }}
              disabled={!canSelectSimilarByNormal}
            />
          </>
        )}
        <MenuItem
          label="Recall Selection"
          icon={<UndoIcon size={13} />}
          onClick={() => {
            recallSelection();
            close();
          }}
          disabled={!hasRecallable}
        />
        <MenuItem
          label="Clear Selection"
          icon={<ClearSelectionIcon size={13} />}
          onClick={() => {
            clearSelection();
            close();
          }}
          disabled={!hasSelection}
        />
        <MenuDivider />
        <MenuItem
          label="Random Select"
          icon={<Dices size={13} />}
          onClick={() => {
            if (!randomExpanded) {
              randomSelect(randomCoverage / 100);
            }
            setRandomExpanded((p) => !p);
          }}
        />
        {randomExpanded && (
          <div style={{ padding: "4px 14px 8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_DIM,
              }}
            >
              <span>Coverage</span>
              <span>{randomCoverage}%</span>
            </div>
            <Slider
              value={randomCoverage}
              min={0}
              max={100}
              step={1}
              onChange={handleRandomCoverageChange}
              defaultValue={50}
              dimChromeOnDrag
            />
          </div>
        )}
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MeshMenuButtons — Vertex / Edge / Face context menus (edit mode only)
// ---------------------------------------------------------------------------

function MeshMenuButtons() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const isCurve = useStore((s) => s.editMode?.isCurve ?? false);
  const open = useStore((s) => s.meshMenuOpen);
  const setOpen = useStore((s) => s.setMeshMenuOpen);
  const setSection = useStore((s) => s.setMeshMenuSection);
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(() => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setAnchorPoint({ x: r.left, y: r.bottom + 4 });
    }
    setSection("all");
    setOpen(!open);
  }, [open, setOpen, setSection, setAnchorPoint]);

  if (workspaceMode !== "edit" || isCurve) return null;

  return (
    <>
      <ToolSep />
      <Tool
        ref={btnRef}
        icon={<MeshMenuIcon size={13} />}
        tip="Mesh"
        active={open}
        onActivate={handleClick}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// HeaderBar — top-left toolbar container (mirrors ToolbarStrip style)
// ---------------------------------------------------------------------------

export function HeaderBar({
  projectOpen,
  onToggleProject,
  debugEnabled,
  debugMenuOpen,
  onToggleDebugMenu,
}: {
  projectOpen: boolean;
  onToggleProject: () => void;
  debugEnabled: boolean;
  debugMenuOpen: boolean;
  onToggleDebugMenu: () => void;
}) {
  const chromeDim = useChromeDim();

  return (
    <ToolSurface edge="top">
      <div
        className="mobile-hscroll"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: HEADER_HEIGHT,
          maxWidth: "100%",
          zIndex: Z_TOOLBAR,
          pointerEvents: "auto",
          background: HEADER_BG,
          borderBottom: `0.5px solid ${COLOR_BORDER}`,
          borderRight: `0.5px solid ${COLOR_BORDER}`,
          borderBottomRightRadius: CORNER_RADIUS,
          display: "flex",
          alignItems: "center",
          padding: "0 6px",
          gap: 4,
          ...chromeDim,
        }}
      >
        <HamburgerMenu open={projectOpen} onToggle={onToggleProject} />

        <SelectionButton />
        <MeshMenuButtons />
        <HierarchyButton />
        <OptionsButton />
        <OperatorsButton />

        {debugEnabled && (
          <>
            <ToolSep />
            <DebugMenu open={debugMenuOpen} onToggle={onToggleDebugMenu} />
          </>
        )}
      </div>
    </ToolSurface>
  );
}
